import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useId } from 'react';
import { cn } from '@/lib/cn.js';

/**
 * Gabarits de page communs (`README-shell.md`, « Gabarits ») : toute page
 * authentifiée se compose d'un `Page` (largeur), d'un `PageHeader` (titre,
 * description, actions, retour) et de `PageSection` (blocs titrés).
 */

/**
 * - `full` : listes, en pleine largeur ;
 * - `detail` : fiches de détail, 1200 px au plus ;
 * - `form` : formulaires de création/édition, 880 px au plus.
 */
export type PageWidth = 'full' | 'detail' | 'form';

const PAGE_WIDTH_CLASS: Record<PageWidth, string> = {
  full: '',
  detail: 'max-w-[1200px]',
  form: 'max-w-[880px]',
};

export interface PageProps {
  width?: PageWidth;
  className?: string | undefined;
  children: ReactNode;
}

/** Conteneur de page : colonne alignée à gauche, sections espacées de 24 px. */
export function Page({ width = 'full', className, children }: PageProps): JSX.Element {
  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-6', PAGE_WIDTH_CLASS[width], className)}>
      {children}
    </div>
  );
}

export type PageBackLink =
  | { label: string; to: string; search?: Record<string, unknown> | undefined }
  | { label: string; onClick: () => void };

export interface PageHeaderProps {
  /** Titre de page (`h1`, 24 px/600). Chaîne seule : c'est le nom accessible de la page. */
  title: string;
  /** Une phrase qui dit à quoi sert l'écran, ou les métadonnées d'une fiche. */
  description?: ReactNode;
  /** Actions de la page, alignées à droite. Une seule action `default` (indigo) au plus. */
  actions?: ReactNode;
  /** Lien de retour vers l'écran parent, affiché au-dessus du titre. */
  backLink?: PageBackLink | undefined;
  /** Badges posés à côté du titre (statut d'une fiche). */
  status?: ReactNode;
  className?: string | undefined;
}

const BACK_LINK_CLASS =
  'inline-flex w-fit items-center gap-1.5 rounded-sm text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** Lien de retour vers l'écran parent — celui de `PageHeader`, exporté pour les vues imbriquées (fiche dans un onglet). */
export function BackLink({ backLink }: { backLink: PageBackLink }): JSX.Element {
  const content = (
    <>
      <ArrowLeft className="size-4" aria-hidden="true" />
      {backLink.label}
    </>
  );
  if ('onClick' in backLink) {
    return (
      <button type="button" className={BACK_LINK_CLASS} onClick={backLink.onClick}>
        {content}
      </button>
    );
  }
  return (
    <Link to={backLink.to} search={backLink.search ?? {}} className={BACK_LINK_CLASS}>
      {content}
    </Link>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  backLink,
  status,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <header className={cn('flex flex-col gap-3', className)}>
      {backLink ? <BackLink backLink={backLink} /> : null}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-6">
        {/* Le titre garde une largeur lisible : ce sont les actions qui passent à la ligne. */}
        <div className="flex min-w-0 flex-col gap-1 md:min-w-64 md:flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="min-w-0 break-words text-2xl font-semibold leading-8 tracking-[-0.01em] text-foreground">
              {title}
            </h1>
            {status ? <div className="flex flex-wrap items-center gap-1.5">{status}</div> : null}
          </div>
          {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2 md:max-w-[65%] md:justify-end md:pt-0.5">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}

export interface PageSectionProps {
  title?: string | undefined;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /**
   * - `panel` (défaut) : surface blanche bordée, pour un bloc de contenu ou de formulaire ;
   * - `plain` : sans cadre, pour un contenu déjà encadré (`DataTable`, tableau, grille de tuiles).
   */
  variant?: 'panel' | 'plain';
  /** Supprime la marge interne du contenu d'un `panel` (tableau collé aux bords). */
  flush?: boolean;
  /** Pied de section (boutons d'enregistrement d'un formulaire). */
  footer?: ReactNode;
  className?: string | undefined;
  contentClassName?: string | undefined;
}

/**
 * Section titrée : titre 16 px, aide courte, actions secondaires à droite.
 * Ne jamais imbriquer deux `panel` — à l'intérieur d'un panneau, séparer les
 * sous-blocs par un titre `h3` et un `border-t`, pas par un nouveau cadre.
 */
export function PageSection({
  title,
  description,
  actions,
  children,
  variant = 'panel',
  flush = false,
  footer,
  className,
  contentClassName,
}: PageSectionProps): JSX.Element {
  const headingId = useId();
  const hasHeader = Boolean(title ?? description ?? actions);
  const isPanel = variant === 'panel';

  const header = hasHeader ? (
    <div
      className={cn(
        'flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4',
        isPanel && 'px-5 pt-4',
        isPanel && flush && 'border-b border-border pb-3',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title ? (
          <h2 id={headingId} className="text-base font-semibold leading-6 text-foreground">
            {title}
          </h2>
        ) : null}
        {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
          {actions}
        </div>
      ) : null}
    </div>
  ) : null;

  if (!isPanel) {
    return (
      <section
        aria-labelledby={title ? headingId : undefined}
        className={cn('flex min-w-0 flex-col gap-3', className)}
      >
        {header}
        {children !== undefined ? (
          <div className={cn('min-w-0', contentClassName)}>{children}</div>
        ) : null}
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2">{footer}</div>
        ) : null}
      </section>
    );
  }

  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      className={cn(
        'min-w-0 rounded-lg border border-border bg-card text-card-foreground',
        className,
      )}
    >
      {header}
      {children !== undefined ? (
        <div
          className={cn(
            'min-w-0',
            !flush && (hasHeader ? 'px-5 pb-5 pt-4' : 'p-5'),
            flush && (hasHeader ? 'overflow-hidden rounded-b-lg' : 'overflow-hidden rounded-lg'),
            flush && Boolean(footer) && 'rounded-b-none',
            contentClassName,
          )}
        >
          {children}
        </div>
      ) : null}
      {footer ? (
        <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-lg border-t border-border bg-muted/40 px-5 py-3">
          {footer}
        </div>
      ) : null}
    </section>
  );
}
